import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Row, Col } from "react-bootstrap";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import api from "../common/api";

const PasswordField = ({ label, value, onChange, showPassword, toggleShow }) => (
  <Form.Group className="mb-4">
    <Form.Label className="fw-semibold" style={{ fontSize: "1.2rem" }}>
      {label}
    </Form.Label>
    <div style={{ position: "relative" }}>
      <Form.Control
        type={showPassword ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={`Enter ${label.toLowerCase()}`}
        style={{ borderRadius: "6px", paddingRight: "40px", paddingBottom: "18px", height: "50px", border: "1px solid #dadadaff", fontSize: "1.1rem", borderColor: "#ccc", }}
        required />
      <span
        onClick={toggleShow}
        style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: showPassword ? "#fc8183ff" : "#444444ff", }}
        aria-label={showPassword ? "Hide password" : "Show password"} >
        {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
      </span>
    </div>
  </Form.Group>
);

const ChangePasswordModal = ({ show, handleClose }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    if (!show) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      setError("");
      setSuccess("");
      setLoading(false);
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

  if (newPassword.trim() !== confirmPassword.trim()) {
      setError("New passwords do not match.");
      return;
    }
  if (!newPassword.trim()) {
    setError("Password is required");
    return;
  } else if (
    newPassword.length < 6 ||
    !/[A-Z]/.test(newPassword) ||
    !/[0-9]/.test(newPassword) ||
    !/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
  ) {
    setError(
      "Password requires at least 6 characters, 1 uppercase, 1 number and 1 special character"
    );
    return;
  }

    try {
      setLoading(true);
      const res = await api.put("/change-password", {
        currentPassword,
        newPassword,
        confirmPassword
      });

      if (res.status === 200) {
        const isDefaultPasswordFlow =
          sessionStorage.getItem("usingDefaultPassword") === "true";
        
          if (isDefaultPasswordFlow) {
            sessionStorage.removeItem("usingDefaultPassword");

            setSuccess("Password changed successfully!");

            setTimeout(() => {
              handleClose();
            }, 1000);

        } else {
        setSuccess("Password changed successfully! Redirecting to login...");
        setTimeout(() => {

          sessionStorage.removeItem("token");
          sessionStorage.removeItem("user");

          handleClose();

          navigate("/login", { replace: true });
        }, 1500);

      }}
    } catch (err) {
      const backendMessage =
        err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : "Something went wrong. Please try again.";
      setError(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      contentClassName="p-5"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton style={{ borderBottom: "none" }}>
        <Modal.Title style={{ fontSize: "2.2rem", color: "white", fontWeight: 700 }}>
          Update Password
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ fontSize: "1.1rem" }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <p className="text-muted mb-4" style={{ fontSize: "1.1rem" }}>
          Create a new password different from your previous one.
        </p>

        <Form onSubmit={handleSubmit}>
          <PasswordField
            label="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            showPassword={showCurrent}
            toggleShow={() => setShowCurrent(!showCurrent)}
          />
          <Row className="mb-4">
            <Col>
              <PasswordField
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                showPassword={showNew}
                toggleShow={() => setShowNew(!showNew)}
              />
            </Col>
            <Col>
              <PasswordField
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                showPassword={showConfirm}
                toggleShow={() => setShowConfirm(!showConfirm)}
              />
            </Col>
          </Row>

          <Form.Text className="text-muted d-block mb-4" style={{ fontSize: "1rem" }}>
           Must be at least 6 characters, include 1 uppercase letter, 1 number, and 1 special character.
          </Form.Text>

          <div className="d-grid">
            <Button
              type="submit"
              disabled={loading}
              style={{
                borderRadius: "6px",
                padding: "12px",
                backgroundColor: "#164788",
                borderColor: "#164788",
                fontWeight: 600,
                fontSize: "1.15rem",
                boxShadow: "0 4px 6px rgba(114, 164, 245, 0.2)",
              }}
            >
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ChangePasswordModal;
